// Circulation wire contracts — loans, the POS copy lookup, holds, fines and
// the Lost & Damaged inventory that db/migrations/0003_circulation.sql backs.
// Browser-safe zod only.
//
// Contract law (pinned):
//   · CopyLookup is the POS BYTE-WISE contract — field names and nullability
//     preserved exactly from the source (circulation.schemas.ts:32-39), incl.
//     openLoan.overdueDays being grace-UNadjusted (the console re-derives the
//     billable fine client-side from group policy).
//   · Money carries the Paise suffix and brand (decisions.md L1 law) — the
//     source's float-rupee `fine`/`amount`/`feeAmount` land as
//     finePaise/amountPaise/feeAmountPaise; semantics are unchanged.
//   · Loans reference the lib_copy SURROGATE id; the barcode is lookup-only.
//     patronId on the wire = lib_member id in storage (routes say /patrons).
//   · Due-date policy moves server-side: loanDays is no longer a bare client
//     field (source sch:15 accepted 1-365 from anyone) — it rides only inside
//     an AUDITED override alongside the issue gates the source left
//     client-side (card expiry, suspension, overdue items, fines threshold,
//     loan limit — ib:103-124).
import { z } from "zod";
import { CursorQuery, EntityId, EpochMs, Paise } from "@erp/core-schemas";
import { CopyStatus, StoredBookCondition } from "./catalogue.js";

/* ────────────────────────────── vocabularies ────────────────────────────── */

/** 'Overdue' is written only by the NEW cron sweep — the source API never wrote it (seed-data-only). */
export const LOAN_STATUSES = ["Active", "Returned", "Overdue"] as const;
export const LoanStatus = z.enum(LOAN_STATUSES);
export type LoanStatus = z.infer<typeof LoanStatus>;

/** 'Expired' arrives via the NEW expiry sweep (or manual PATCH — source parity). */
export const HOLD_STATUSES = ["Pending", "Ready", "Expired", "Completed"] as const;
export const HoldStatus = z.enum(HOLD_STATUSES);
export type HoldStatus = z.infer<typeof HoldStatus>;

/** Unpaid → Paid|Waived only (settle guard); Waived also via inventory markFound. */
export const FINE_STATUSES = ["Unpaid", "Paid", "Waived"] as const;
export const FineStatus = z.enum(FINE_STATUSES);
export type FineStatus = z.infer<typeof FineStatus>;

/** Return-desk condition verdict (source sch:20). */
export const RETURN_CONDITIONS = ["Good", "Damaged", "Lost"] as const;
export const ReturnCondition = z.enum(RETURN_CONDITIONS);
export type ReturnCondition = z.infer<typeof ReturnCondition>;

/**
 * The replacement/repair fee reasons the API itself writes (svc:311). The CODE
 * string 'Damaged Item' is the contract — the source schema comment's
 * 'Damaged Copy' (db:239) is stale. Fine reason stays an OPEN string overall:
 * manual creates accept free text.
 */
export const REPLACEMENT_REASONS = ["Lost Item", "Damaged Item"] as const;

/* ─────────────────────────────────── loans ─────────────────────────────────── */

/**
 * Source LoanSchema plus the surrogate copyId. copyBarcode/bookTitle/
 * patronName read the denormalized issue-time snapshots; `branch` stays
 * live-derived (copy → branch join — it tracks the copy's CURRENT location).
 */
export const Loan = z.object({
  id: EntityId, // source 'loan-…' ids port verbatim; new rows mint ULIDs
  copyId: EntityId, // NEW — the surrogate FK; barcode is lookup-only
  copyBarcode: z.string(),
  bookTitle: z.string().nullable(),
  patronId: EntityId, // ← lib_loan.member_id
  patronName: z.string().nullable(),
  branch: z.string().nullable(),
  issueDate: EpochMs,
  dueDate: EpochMs,
  returnDate: EpochMs.nullable(),
  renewalsCount: z.number().int().min(0),
  status: LoanStatus, // source emitted an open string — the vocab was always these three
});
export type Loan = z.infer<typeof Loan>;

/**
 * The audited desk override — replaces the console's un-audited bypass
 * checkbox (ib:41,124,444) and its client-chosen due date (ib:42-43,235).
 * Present → the service records reason + actor and may bypass the member
 * gates / extend the rule-derived loan period within the bound.
 */
export const CirculationOverride = z.object({
  reason: z.string().trim().min(1).max(500),
  /** Bounded due-date override — absent means the rule-derived loan period applies. */
  loanDays: z.number().int().min(1).max(365).optional(),
});
export type CirculationOverride = z.infer<typeof CirculationOverride>;

/**
 * POST /circulation/issue. Cart-shaped: the desk issues one or many copies to
 * one patron (the source console looped a client-side cart over single-barcode
 * POSTs). issuedBy is GONE from the body — the api stamps the authenticated
 * staff session (the source defaulted it to the borrower, svc:68).
 */
export const IssueBody = z.object({
  barcodes: z.array(z.string().trim().min(1)).min(1).max(50),
  patronId: EntityId,
  override: CirculationOverride.optional(),
});
export type IssueBody = z.infer<typeof IssueBody>;

/** 201 — one Loan per issued barcode, in request order. */
export const IssueResult = z.object({ loans: z.array(Loan).min(1) });
export type IssueResult = z.infer<typeof IssueResult>;

/** POST /circulation/return (source sch:18-21 parity). */
export const ReturnBody = z.object({
  barcode: z.string().trim().min(1),
  condition: ReturnCondition.default("Good"),
});
export type ReturnBody = z.infer<typeof ReturnBody>;

/**
 * Source ReturnResult (sch:23-26) with `fine` renamed finePaise (paise law —
 * semantics preserved): the TOTAL billed on this return (grace-adjusted
 * overdue fine + Lost/Damaged fee); fineId prefers the overdue fine's id,
 * else the fee's, else null — the desk auto-settle consumes the pair.
 */
export const ReturnResult = z.object({
  loanId: EntityId,
  /** Raw days past due — grace-UNadjusted (grace applies to the billed amount only). */
  overdueDays: z.number().int().min(0),
  finePaise: Paise,
  fineId: EntityId.nullable(),
  /** Patron id whose hold this return promoted to Ready (else null). */
  holdReadyForPatron: EntityId.nullable(),
});
export type ReturnResult = z.infer<typeof ReturnResult>;

/**
 * POST /circulation/loans/:id/renew. The source's bare client-chosen
 * loanDays (sch:28) becomes rule-derived; extensions ride the audited
 * override, symmetric with IssueBody.
 */
export const RenewBody = z.object({ override: CirculationOverride.optional() });
export type RenewBody = z.infer<typeof RenewBody>;

/**
 * Keyset list on (issue_date DESC, id). ?status is exact-eq; ?branch matches
 * the copy's CURRENT branch by name (source parity); search LIKEs over the
 * denormalized member_name/book_title/copy_barcode snapshots (the source
 * LIKE'd the same trio through 4 joins).
 */
export const LoanListQuery = CursorQuery.extend({
  status: LoanStatus.optional(),
  branch: z.string().trim().min(1).max(120).optional(),
  search: z.string().trim().min(1).max(200).optional(),
});
export type LoanListQuery = z.infer<typeof LoanListQuery>;

/* ─────────────── copy lookup (the POS byte-wise contract) ─────────────── */

export const OpenLoan = z.object({
  id: EntityId,
  patronId: EntityId,
  patronName: z.string().nullable(),
  issueDate: EpochMs,
  dueDate: EpochMs,
  /** max(0, floor((now-dueDate)/DAY)) — grace-UNadjusted (svc:163, pinned). */
  overdueDays: z.number().int().min(0),
});
export type OpenLoan = z.infer<typeof OpenLoan>;

/**
 * GET /circulation/lookup/:barcode — field names/nullability preserved
 * byte-for-byte from the source (sch:32-39): no copyId here, bookId stays
 * nullable (the target column is NOT NULL — nullability kept for the wire),
 * available is strictly copyStatus === 'Available'. openLoan = the first loan
 * on the copy with status != 'Returned', else null. 404 on unknown barcode.
 */
export const CopyLookup = z.object({
  barcode: z.string(),
  bookId: EntityId.nullable(),
  bookTitle: z.string().nullable(),
  copyStatus: CopyStatus, // source emitted an open string; the vocab is the copy enum
  available: z.boolean(),
  branch: z.string().nullable(),
  openLoan: OpenLoan.nullable(),
});
export type CopyLookup = z.infer<typeof CopyLookup>;

/* ─────────────────────────────────── holds ─────────────────────────────────── */

/** TITLE-level holds (bookId, never copy-level — db:225 pinned). */
export const Hold = z.object({
  id: EntityId, // source 'hold-…' ids port verbatim
  bookId: EntityId,
  bookTitle: z.string().nullable(),
  patronId: EntityId, // ← lib_hold.member_id
  patronName: z.string().nullable(),
  requestDate: EpochMs, // ← lib_hold.placed_at
  status: HoldStatus,
  queuePosition: z.number().int().min(1),
  /** Stamped on Pending holds too (source svc:192 — characterized wart, carried). */
  expirationDate: EpochMs.nullable(), // ← lib_hold.expires_at
});
export type Hold = z.infer<typeof Hold>;

export const CreateHold = z.object({ bookId: EntityId, patronId: EntityId });
export type CreateHold = z.infer<typeof CreateHold>;

/**
 * PATCH /circulation/holds/:id — the WHOLE surface is the status (source
 * parity; transitions stay unguarded, no queue rebalance, no copy side
 * effects — characterized).
 */
export const UpdateHold = z.object({ status: HoldStatus });
export type UpdateHold = z.infer<typeof UpdateHold>;

/** Keyset list on (queue_position ASC, id) — the console's queue order (svc:178). */
export const HoldListQuery = CursorQuery.extend({
  status: HoldStatus.optional(),
  bookId: EntityId.optional(),
  patronId: EntityId.optional(),
  /** Book-title contains-match (the queue's free-text search). */
  search: z.string().trim().min(1).max(200).optional(),
});
export type HoldListQuery = z.infer<typeof HoldListQuery>;

/* ─────────────────────────────────── fines ─────────────────────────────────── */

export const Fine = z.object({
  id: EntityId, // source 'fine-…' ids port verbatim
  patronId: EntityId, // ← lib_fine.member_id
  patronName: z.string().nullable(),
  patronCard: z.string().nullable(),
  loanId: EntityId.nullable(), // manual fines detach (source parity)
  bookTitle: z.string().nullable(),
  reason: z.string(), // open vocabulary — manual creates are free text
  /** Whole days the underlying loan ran past due (return date, else fine date) — null for detached/on-time fines. */
  daysLate: z.number().int().nullable(),
  amountPaise: Paise, // ← amount_paise (source `amount` REAL rupees, ×100 at ETL)
  status: FineStatus,
  /** Opaque FIN-module invoice ref — null until billing lands. */
  invoiceId: EntityId.nullable(),
  createdAt: EpochMs,
  settledAt: EpochMs.nullable(),
  notes: z.string().nullable(),
});
export type Fine = z.infer<typeof Fine>;

/** POST /circulation/fines — always born 'Unpaid'; reason defaults 'Other' (svc:226 parity). */
export const CreateFine = z.object({
  patronId: EntityId,
  amountPaise: Paise.refine((p) => p > 0, "amount must be positive paise"),
  reason: z.string().trim().min(1).max(120).default("Other"),
  notes: z.string().max(500).optional(),
  loanId: EntityId.optional(),
});
export type CreateFine = z.infer<typeof CreateFine>;

/**
 * POST /circulation/fines/:id/settle — only 'Unpaid' settles; supplied notes
 * OVERWRITE the stored notes, omitted notes keep them (svc:239 —
 * characterized).
 */
export const SettleFineBody = z.object({
  action: z.enum(["pay", "waive"]),
  notes: z.string().max(500).optional(),
  /** Opaque FIN invoice ref to stamp on settle (the FIN settlement seam) — omitted keeps the stored value. */
  invoiceId: EntityId.optional(),
});
export type SettleFineBody = z.infer<typeof SettleFineBody>;

/** Keyset list on (created_at DESC, id); ?status exact-eq (source parity), ?patronId rides the (member_id,status) index. */
export const FineListQuery = CursorQuery.extend({
  status: FineStatus.optional(),
  patronId: EntityId.optional(),
  /** Exact reason match ('Overdue Return' | 'Lost Item' | …). */
  reason: z.string().trim().min(1).max(60).optional(),
});
export type FineListQuery = z.infer<typeof FineListQuery>;

/* ─────────────────────── Lost & Damaged inventory ─────────────────────── */

/** Source FlaggedCopySchema (sch:103-108) with feeAmount → feeAmountPaise (paise law). */
export const FlaggedCopy = z.object({
  barcode: z.string(),
  bookId: EntityId.nullable(),
  bookTitle: z.string().nullable(),
  branch: z.string().nullable(),
  status: CopyStatus, // 'Lost' | 'Damaged' in practice (the listing filter)
  bookCondition: StoredBookCondition.nullable(),
  lastBorrowerId: EntityId.nullable(),
  lastBorrowerName: z.string().nullable(),
  /** last loan's returnDate, else the copy's acquiredDate (svc:332). */
  flaggedSince: EpochMs,
  feeId: EntityId.nullable(),
  feeAmountPaise: Paise.nullable(),
  feeStatus: FineStatus.nullable(),
});
export type FlaggedCopy = z.infer<typeof FlaggedCopy>;

/** POST /circulation/inventory/:barcode/mark-lost — bills patronId ?? last borrower ?? nobody. */
export const MarkLostBody = z.object({
  condition: z.enum(["Lost", "Damaged"]).default("Lost"), // mirrors svc:358
  patronId: EntityId.optional(),
});
export type MarkLostBody = z.infer<typeof MarkLostBody>;

export const MarkLostResult = z.object({
  barcode: z.string(),
  status: CopyStatus,
  feeId: EntityId.nullable(),
});
export type MarkLostResult = z.infer<typeof MarkLostResult>;

/** POST /circulation/inventory/:barcode/found — back to Available, open replacement/repair fines waived. */
export const MarkFoundResult = z.object({ barcode: z.string(), status: CopyStatus });
export type MarkFoundResult = z.infer<typeof MarkFoundResult>;
